// AgentConfigService.js - Updated with TAB_ORDER and Feedback config support

class AgentConfigService {
  // Base URL constants
  static API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:9002';
  static CONFIG_PATH = '/api/config';
  
  // Default tab order fallback
  static DEFAULT_TAB_ORDER = ['HUB', 'RCM', 'COLAB', 'FARM', 'MCP', 'COUNSEL'];

  /**
   * Load unified role configuration
   * This config combines features, default tab, tab order, and agent mappings per role
   * @param {string} sessionId - Session ID for authentication
   * @returns {Promise<Object>} Unified configuration object
   */
  static async loadUnifiedConfig(sessionId) {
    try {
      // First check localStorage cache
      const cachedConfig = localStorage.getItem('unified_config');
      if (cachedConfig) {
        try {
          return JSON.parse(cachedConfig);
        } catch (e) {
          console.warn('Invalid cached unified config, fetching fresh');
          localStorage.removeItem('unified_config');
        }
      }
      
      // Fetch from API
      console.log('Fetching unified config from API');
      const mainApiUrl = await this.getMainApiUrl();
      
      const response = await fetch(`${mainApiUrl}/api/config/unified`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load unified config: ${response.status}`);
      }
      
      const data = await response.json();
      localStorage.setItem('unified_config', JSON.stringify(data));
      return data;

    } catch (error) {
      console.error('Error loading unified config:', error);
      return this.getUnifiedConfigFallback();
    }
  }

  /**
   * Get unified config fallback
   */
  static getUnifiedConfigFallback() {
    const fallbackConfig = {
      "TAB_ORDER": ["HUB", "RCM", "COLAB", "MCP", "COUNSEL"],
      "testrole": {
        "DEFAULT": "HUB",
        "HUB": {
          "Collaborative Process Agents": ["agent100001", "agent100002"],
          "Autonomous Process Agents": ["agent100005"],
          "Knowledge Agents": []
        },
        "RCM": {
          "Collaborative Process Agents": ["agent100001", "agent100002"]
        },
        "COLAB": {},
        "MCP": {}
      },
      "admin": {
        "DEFAULT": "COLAB",
        "HUB": {
          "Collaborative Process Agents": ["agent100001"],
          "Autonomous Process Agents": ["agent100003"],
          "Knowledge Agents": ["agent100002"]
        },
        "COLAB": {}
      },
      "user": {
        "DEFAULT": "HUB",
        "HUB": {
          "Collaborative Process Agents": ["agent100001"],
          "Knowledge Agents": ["agent100002"]
        }
      }
    };
    
    console.log('Using fallback unified config');
    return fallbackConfig;
  }

  /**
   * Get tab order from unified config or localStorage
   * @param {string} sessionId - Session ID (optional, for API fetch)
   * @returns {Promise<Array>} Tab order array
   */
  static async getTabOrder(sessionId = null) {
    try {
      // First check localStorage for cached tab order
      const cachedTabOrder = localStorage.getItem('tab_order');
      if (cachedTabOrder) {
        try {
          return JSON.parse(cachedTabOrder);
        } catch (e) {
          console.warn('Invalid cached tab order');
          localStorage.removeItem('tab_order');
        }
      }

      // If we have a session, try to get from unified config
      if (sessionId) {
        const config = await this.loadUnifiedConfig(sessionId);
        if (config && config.tab_order) {
          localStorage.setItem('tab_order', JSON.stringify(config.tab_order));
          return config.tab_order;
        }
      }

      // Return default
      return this.DEFAULT_TAB_ORDER;
    } catch (error) {
      console.error('Error getting tab order:', error);
      return this.DEFAULT_TAB_ORDER;
    }
  }

  /**
   * Get tab order synchronously from localStorage (for immediate rendering)
   * @returns {Array} Tab order array
   */
  static getTabOrderSync() {
    try {
      const cachedTabOrder = localStorage.getItem('tab_order');
      if (cachedTabOrder) {
        return JSON.parse(cachedTabOrder);
      }
    } catch (e) {
      console.warn('Error reading cached tab order');
    }
    return this.DEFAULT_TAB_ORDER;
  }

  /**
   * Get default tab for a role from unified config
   * @param {string} role - User role
   * @param {string} sessionId - Session ID
   * @returns {Promise<string>} Default tab name
   */
  static async getDefaultTab(role, sessionId) {
    try {
      const config = await this.loadUnifiedConfig(sessionId);
      const roleConfig = config[role] || {};
      return roleConfig['DEFAULT'] || 'HUB';
    } catch (error) {
      console.error('Error getting default tab:', error);
      return 'HUB';
    }
  }

  /**
   * Get features (tabs) for a role from unified config
   * Features are derived from the object keys (excluding 'DEFAULT')
   * @param {string} role - User role
   * @param {string} sessionId - Session ID
   * @returns {Promise<Array>} Array of feature names
   */
  static async getFeaturesForRole(role, sessionId) {
    try {
      const config = await this.loadUnifiedConfig(sessionId);
      const roleConfig = config[role] || {};
      
      // Get all keys except 'DEFAULT'
      const features = Object.keys(roleConfig).filter(key => key !== 'DEFAULT');
      return features.length > 0 ? features : ['HUB', 'COLAB'];
    } catch (error) {
      console.error('Error getting features for role:', error);
      return ['HUB', 'COLAB'];
    }
  }

  /**
   * Get agents for a specific feature/tab from unified config
   * @param {string} role - User role
   * @param {string} feature - Feature/tab name (e.g., 'HUB', 'RCM')
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Agent groups for the feature
   */
  static async getAgentsForFeature(role, feature, sessionId) {
    try {
      const config = await this.loadUnifiedConfig(sessionId);
      const roleConfig = config[role] || {};
      return roleConfig[feature] || {};
    } catch (error) {
      console.error('Error getting agents for feature:', error);
      return {};
    }
  }

  /**
   * Map tab name to route path
   * @param {string} tab - Tab name
   * @returns {string} Route path
   */
  static getRouteForTab(tab) {
    const routes = {
      'HUB': '/agent-hub',
      'RCM': '/rcm',
      'COLAB': '/agent-colab',
      'MCP': '/mcp',
      'FARM': '/farm',
      'COUNSEL': '/counsel'
    };
    return routes[tab] || '/agent-hub';
  }

  /**
   * Get default route for a role
   * @param {string} role - User role
   * @param {string} sessionId - Session ID
   * @returns {Promise<string>} Default route path
   */
  static async getDefaultRoute(role, sessionId) {
    const defaultTab = await this.getDefaultTab(role, sessionId);
    return this.getRouteForTab(defaultTab);
  }

  /**
   * Clear config cache including tab order and feedback config
   */
  static clearConfigCache() {
    localStorage.removeItem('agent_config');
    localStorage.removeItem('role_agent_config');
    localStorage.removeItem('common_config');
    localStorage.removeItem('unified_config');
    localStorage.removeItem('tab_order');
    localStorage.removeItem('feedback_config');
    console.log('Config cache cleared');
  }

  /**
   * Load common configuration
   * @returns {Promise<Object>} Common configuration object
   */
  static async loadCommonConfig(sessionId) {
    try {
      // First check localStorage cache
      const cachedConfig = localStorage.getItem('common_config');
      if (cachedConfig) {
        try {
          return JSON.parse(cachedConfig);
        } catch (e) {
          console.warn('Invalid cached common config, fetching fresh');
          localStorage.removeItem('common_config');
        }
      }
      
      // Always fetch from API
      console.log('Fetching common config from API');
      
      const response = await fetch(`${this.API_BASE_URL}/api/config/common`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load common config: ${response.status}`);
      }
      
      const data = await response.json();
      localStorage.setItem('common_config', JSON.stringify(data));
      return data;
      
    } catch (error) {
      console.error('Error loading common config:', error);
      return this.getCommonConfigFallback();
    }
  }
  
  /**
   * Get common config fallback
   */
  static getCommonConfigFallback() {
    const fallbackConfig = {
      "MAIN_API_URL": "http://localhost:9002",
      "APP_NAME": "Healthcare AI Deployment Platform",
      "VERSION": "1.0.0",
      "DEFAULT_AGENT_ICON": "default.png",
      "DEFAULT_USER_ROLE": "testrole",
      "SESSION_TIMEOUT_MINUTES": 480,
      "ENVIRONMENT": "development",
      "API_TIMEOUT_MS": 30000,
      "DEFAULT_ERROR_MESSAGE": "An error occurred. Please try again later.",
      "ENABLE_ANALYTICS": false,
      "document_rules_extraction": "http://localhost:8013",
      "vector_store_service": "http://localhost:8020"
    };
    
    console.log('Using fallback common config');
    return fallbackConfig;
  }
  
  /**
   * Get the main API URL from configuration or environment variables
   * @returns {Promise<string>} The main API URL
   */
  static async getMainApiUrl() {
    return process.env.REACT_APP_MAIN_API_URL || 'http://localhost:9002';
    //return 'http://localhost:9002'
  }

  /**
   * Load feedback configuration for all agents
   * @param {string} sessionId - Session ID for authentication
   * @returns {Promise<Object>} Feedback configuration object
   */
  static async loadFeedbackConfig(sessionId) {
    try {
      // First check localStorage cache
      const cachedConfig = localStorage.getItem('feedback_config');
      if (cachedConfig) {
        try {
          return JSON.parse(cachedConfig);
        } catch (e) {
          console.warn('Invalid cached feedback config, fetching fresh');
          localStorage.removeItem('feedback_config');
        }
      }
      
      // Fetch from API
      console.log('Fetching feedback config from API');
      const mainApiUrl = await this.getMainApiUrl();
      
      const response = await fetch(`${mainApiUrl}/api/config/feedback`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load feedback config: ${response.status}`);
      }
      
      const data = await response.json();
      localStorage.setItem('feedback_config', JSON.stringify(data));
      return data;
      
    } catch (error) {
      console.error('Error loading feedback config:', error);
      return this.getFeedbackConfigFallback();
    }
  }

  /**
   * Get feedback config for a specific agent
   * @param {string} agentId - Agent ID
   * @param {string} sessionId - Session ID for authentication
   * @returns {Promise<Object>} Feedback configuration for the agent
   */
  static async getFeedbackConfigForAgent(agentId, sessionId) {
    try {
      const allConfig = await this.loadFeedbackConfig(sessionId);
      return allConfig[agentId] || this.getDefaultFeedbackConfig();
    } catch (error) {
      console.error(`Error getting feedback config for agent ${agentId}:`, error);
      return this.getDefaultFeedbackConfig();
    }
  }


  static async getWorkQueueServiceUrl_old() {
    try {
      const config  = await AgentConfigService.loadConfig();  
      const baseUrl = config?.work_queue_service?.base_url;
      if (baseUrl) return baseUrl;
    } catch (e) {
      console.warn('Could not read work_queue_service base_url from config:', e);
    }
    return 'http://localhost:8045';   // fallback matches service default port
  }


  static async getWorkQueueServiceUrl() {
      return process.env.REACT_APP_WQ_SERVICE_URL || 'http://localhost:8045';
  }


  static async getAuditServiceUrl() {
    return process.env.REACT_APP_AUDIT_SERVICE_URL || 'http://localhost:9008';
  }


  static async getTelemetryServiceUrl() {
    return process.env.REACT_APP_TELEMETRY_SERVICE_URL || 'http://localhost:9009';
}

  /**
   * Get feedback config synchronously from localStorage
   * @param {string} agentId - Agent ID
   * @returns {Object} Feedback configuration for the agent
   */
  static getFeedbackConfigSync(agentId) {
    try {
      const cachedConfig = localStorage.getItem('feedback_config');
      if (cachedConfig) {
        const allConfig = JSON.parse(cachedConfig);
        return allConfig[agentId] || this.getDefaultFeedbackConfig();
      }
    } catch (e) {
      console.warn('Error reading cached feedback config');
    }
    return this.getDefaultFeedbackConfig();
  }

  /**
   * Get default feedback config (used when no config is defined for an agent)
   * @returns {Object} Default feedback configuration
   */
  static getDefaultFeedbackConfig() {
    return {
      options: [],
      input_type: "checkbox"
    };
  }

  /**
   * Get feedback config fallback
   */
  static getFeedbackConfigFallback() {
    const fallbackConfig = {
      "agent100001": {
        "options": [
          { "id": "incomplete", "label": "Incomplete understanding" },
          { "id": "wrong_focus", "label": "Wrong focus" },
          { "id": "incorrect_facts", "label": "Incorrect facts" },
          { "id": "others", "label": "Others" }
        ],
        "input_type": "checkbox"
      },
      "agent100002": {
        "options": [
          { "id": "incomplete", "label": "Incomplete understanding" },
          { "id": "wrong_focus", "label": "Wrong focus" },
          { "id": "incorrect_facts", "label": "Incorrect facts" },
          { "id": "others", "label": "Others" }
        ],
        "input_type": "checkbox"
      }
    };
    
    console.log('Using fallback feedback config');
    return fallbackConfig;
  }
  
  /**
   * Load all agent configuration
   * @returns {Promise<Object>} Object with all agent configurations
   */
  static async loadAllAgentConfig() {
    console.log('Inside loadAllAgentConfig')
    // Check localStorage cache first
    const cachedConfig = localStorage.getItem('agent_config');
    if (cachedConfig) {
      try {
        return JSON.parse(cachedConfig);
      } catch (e) {
        console.warn('Invalid cached agent config, fetching fresh');
        localStorage.removeItem('agent_config');
      }
    }
    
    try {
      // Always fetch from API
      console.log('Fetching agent config from API');
      const sessionId = sessionStorage.getItem('sessionId') || localStorage.getItem('sessionId');
      
      if (!sessionId) {
        throw new Error('No session ID found for API request');
      }
      
      const mainApiUrl = await this.getMainApiUrl();
      console.log(mainApiUrl, "........")
      
      const response = await fetch(`${mainApiUrl}/api/config/agents`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load agent config: ${response.status}`);
      }
      
      const config = await response.json();
      localStorage.setItem('agent_config', JSON.stringify(config));
      return config;
      
    } catch (error) {
      console.error('Error loading agent config:', error);
      return this.getAgentConfigFallback();
    }
  }
  
  static async loadWorkflowAgents() {
    try {
      const mainApiUrl = await this.getMainApiUrl();
      const response = await fetch(`${mainApiUrl}/api/workflow-agents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to load workflow agents');
      }

      return result.data;

    } catch (error) {
      console.error('AgentConfigService.loadWorkflowAgents error:', error);
      throw new Error(`Failed to load workflow agents: ${error.message}`);
    }
  }

  /**
   * Get agent config fallback
   */
  static getAgentConfigFallback() {
    const fallbackConfig = {
      "agent100001": {
        "id": "agent100001",
        "name": "Eligibility Agent",
        "icon": "window-s4.png",
        "endpoint": "http://localhost:8011",
        "outcome_text": "All Possible Outcome",
        "layout_columns": 3
      },
      "agent100002": {
        "id": "agent100002",
        "name": "Service Auth Check Agent",
        "icon": "check-s4.png",
        "endpoint": "http://localhost:8012",
        "outcome_text": "Medical Policy Criteria Check",
        "layout_columns": 3
      },
      "agent100003": {
        "id": "agent100003",
        "name": "Auth Builder Agent",
        "icon": "clipboard-s4.png",
        "endpoint": "http://localhost:8013",
        "outcome_text": "Auth Builder outcome"
      },
      "agent100004": {
        "id": "agent100004",
        "name": "Medical Policy Q&A Agent",
        "icon": "books-s4.png",
        "endpoint": "http://localhost:8014",
        "outcome_text": "Medical Policy Q&A",
        "layout_columns": 2
      },
      "agent100005": {
        "id": "agent100005",
        "name": "ASO Review Agent",
        "icon": "clipboard-s4.png",
        "endpoint": "http://localhost:8015",
        "outcome_text": "ASO Claims Review Result",
        "layout_columns": 1
      },
      "agent100006": {
        "id": "agent100006",
        "name": "Oncology Research Assistant",
        "icon": "edit-s4.png",
        "endpoint": "http://localhost:8005",
        "outcome_text": "Oncology Research Insights"
      }
    };
    
    console.log('Using fallback agent config');
    return fallbackConfig;
  }
  
  /**
   * Load the role-agent mapping configuration
   * @returns {Promise<Object>} Object mapping roles to agent groups
   */
  static async loadRoleAgentConfig() {
    // Check localStorage cache first
    const cachedConfig = localStorage.getItem('role_agent_config');
    if (cachedConfig) {
      try {
        return JSON.parse(cachedConfig);
      } catch (e) {
        console.warn('Invalid cached role-agent config, fetching fresh');
        localStorage.removeItem('role_agent_config');
      }
    }
    
    try {
      // Always fetch from API
      console.log('Fetching role-agent config from API');
      const sessionId = sessionStorage.getItem('sessionId') || localStorage.getItem('sessionId');
      
      if (!sessionId) {
        throw new Error('No session ID found for API request');
      }
      
      const mainApiUrl = await this.getMainApiUrl();
      
      const response = await fetch(`${mainApiUrl}/api/config/role-agents`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load role-agent config: ${response.status}`);
      }
      
      const config = await response.json();
      localStorage.setItem('role_agent_config', JSON.stringify(config));
      return config;
      
    } catch (error) {
      console.error('Error loading role-agent config:', error);
      return this.getRoleAgentConfigFallback();
    }
  }
  
  /**
   * Get role-agent config fallback
   */
  static getRoleAgentConfigFallback() {
    const fallbackConfig = {
      "testrole": {
        "Collaborative Process Agents": ["agent100001", "agent100002"],
        "Autonomous Process Agents": ["agent100005"],
        "Knowledge Agents": ["agent100004", "agent100006"]
      },
      "admin": {
        "Collaborative Process Agents": ["agent100001"],
        "Autonomous Process Agents": ["agent100003"],
        "Knowledge Agents": ["agent100002", "agent100004"]
      },
      "user": {
        "Collaborative Process Agents": ["agent100001"],
        "Knowledge Agents": ["agent100002", "agent100004"]
      }
    };
    
    console.log('Using fallback role-agent config');
    return fallbackConfig;
  }
  
  /**
   * Get the API URL for a specific agent
   * @param {string} agentId - The agent ID
   * @returns {Promise<string>} The agent API URL
   */
  static async getAgentApiUrl(agentId) {
    try {
      const allAgentConfig = await this.loadAllAgentConfig();
      
      if (allAgentConfig && allAgentConfig[agentId] && allAgentConfig[agentId].endpoint) {
        return allAgentConfig[agentId].endpoint;
      } else {
        throw new Error(`Agent ${agentId} not found in configuration`);
      }
    } catch (error) {
      console.error(`Error getting API URL for agent ${agentId}:`, error);
      throw error;
    }
  }
  
  /**
   * Get service URL for specific services
   * @returns {Promise<string>} The service URL
   */
  static async getServiceUrl(serviceName) {
    try {
      const config = await this.loadCommonConfig();
      
      if (config[serviceName]) {
        return config[serviceName];
      }
      
      const serviceUrls = {
        'document_rules_extraction': config.document_rules_extraction || 'http://localhost:8013',
        'vector_store_service': config.vector_store_service || 'http://localhost:8020'
      };
      
      const url = serviceUrls[serviceName];
      if (!url) {
        throw new Error(`Unknown service: ${serviceName}`);
      }
      
      return url;
    } catch (error) {
      console.error('Error getting service URL:', error);
      throw error;
    }
  }
  
  /**
   * Get all projects for the current user
   * @param {string} sessionId - The user's session ID
   * @returns {Promise<Array>} Array of project objects
   */
  static async getProjects(sessionId) {
    try {
      const mainApiUrl = await this.getMainApiUrl();
      const response = await fetch(`${mainApiUrl}/api/projects`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get projects: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting projects:', error);
      return [];
    }
  }
  
  /**
   * Create a new project
   * @param {string} sessionId - The user's session ID
   * @param {Object} projectData - The project data
   * @returns {Promise<Object>} The created project
   */
  static async createProject(sessionId, projectData) {
    try {
      const mainApiUrl = await this.getMainApiUrl();
      const response = await fetch(`${mainApiUrl}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(projectData),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create project: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  }
  
  /**
   * Get saved sessions for the current user
   * @param {string} sessionId - The user's session ID
   * @returns {Promise<Array>} Array of saved sessions
   */
  static async getSavedSessions(sessionId) {
    try {
      const mainApiUrl = await this.getMainApiUrl();
      const response = await fetch(`${mainApiUrl}/api/sessions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get saved sessions: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting saved sessions:', error);
      return [];
    }
  }
  
  /**
   * Load a saved session
   * @param {string} sessionId - The user's session ID
   * @param {string} savedSessionId - The saved session ID to load
   * @returns {Promise<Object>} The saved session data
   */
  static async loadSavedSession(sessionId, savedSessionId) {
    try {
      const mainApiUrl = await this.getMainApiUrl();
      const response = await fetch(`${mainApiUrl}/api/sessions/${savedSessionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load saved session: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error loading saved session ${savedSessionId}:`, error);
      throw error;
    }
  }
  
  /**
   * Save the current session
   * @param {string} sessionId - The user's session ID
   * @param {string} agentId - The agent ID
   * @param {Object} sessionData - The session data to save
   * @param {string} projectId - Optional project ID
   * @returns {Promise<Object>} The saved session
   */
  static async saveSession(sessionId, agentId, sessionData, projectId = null) {
    try {
      const mainApiUrl = await this.getMainApiUrl();
      const payload = {
        agentId,
        sessionData
      };
      
      if (projectId) {
        payload.projectId = projectId;
      }
      
      const response = await fetch(`${mainApiUrl}/api/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save session: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error saving session:', error);
      throw error;
    }
  }
  
  /**
   * Load master data configuration
   * @returns {Promise<Object>} Master data configuration
   */
  static async loadMasterData() {
    try {
      const cachedMasterData = localStorage.getItem('master_data_config');
      if (cachedMasterData) {
        try {
          console.log('Loading master data from cache');
          return JSON.parse(cachedMasterData);
        } catch (e) {
          console.warn('Invalid cached master data, fetching fresh');
          localStorage.removeItem('master_data_config');
        }
      }
      
      console.log('Fetching master data from API');
      const sessionId = sessionStorage.getItem('sessionId') || localStorage.getItem('sessionId');
      
      if (!sessionId) {
        throw new Error('No session ID found for API request');
      }
      
      const mainApiUrl = await this.getMainApiUrl();
      
      const response = await fetch(`${mainApiUrl}/api/config/master-data`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error("Master data not found on API, returning empty");
        return {};
      }
      
      const data = await response.json();
      console.log('Master data loaded successfully:', data);
      localStorage.setItem('master_data_config', JSON.stringify(data));
      
      return data;
    } catch (error) {
      console.error('Error loading master data:', error);
      return {};
    }
  }

  /**
   * Submit feedback for an agent session
   * @param {string} sessionId - Session ID for authentication
   * @param {Object} feedbackData - Feedback data to submit
   * @returns {Promise<Object>} Response from feedback submission
   */
  static async submitFeedback(sessionId, feedbackData) {
    try {
      const mainApiUrl = await this.getMainApiUrl();
      
      const response = await fetch(`${mainApiUrl}/api/feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(feedbackData),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.status}`);
      }
      
      return await response.json();
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }


 /**
   * Load agent help content from a static markdown file in public/help/.
   * Files are named by agentId: public/help/<agentId>.md
   * These are served as static assets — no backend call needed.
   *
   * @param {string} agentId - The agent ID (used as the filename)
   * @returns {Promise<string|null>} Markdown string, or null if file not found
   */
  static async loadAgentHelp(agentId) {
    if (!agentId) return null;
 
    try {
      // In-memory cache to avoid re-fetching within the same page session
      if (!AgentConfigService._helpCache) {
        AgentConfigService._helpCache = {};
      }
 
      if (agentId in AgentConfigService._helpCache) {
        return AgentConfigService._helpCache[agentId];
      }
 
      const url = `/help/${agentId}.md`;
      const response = await fetch(url);
 
      if (response.status === 404) {
        // No help file for this agent — not an error, just unavailable
        AgentConfigService._helpCache[agentId] = null;
        return null;
      }
 
      if (!response.ok) {
        throw new Error(`Failed to load help for ${agentId}: ${response.status}`);
      }
 
      const markdown = await response.text();
      AgentConfigService._helpCache[agentId] = markdown;
      return markdown;
 
    } catch (error) {
      console.error(`Error loading help content for agent ${agentId}:`, error);
      return null;
    }
  }
}



export default AgentConfigService;